
import { Property } from "csstype"
import { ReactNode } from "react"
import { defineMessages } from "react-intl"
import { Message } from "../../../helpers/IntlProps"
import { AccountStatementsColumnIds } from "./account-statements"
import { AppointmentsColumnIds } from "./calendar-events"
import { CompaniesColumnIds } from "./companies"
import { ContactsColumnIds } from "./contacts"
import { CreditMemosColumnIds } from "./credit-memos"
import { InventoryStockDocumentColumnIds } from "./inventory-stock-documents"
import { InvoiceColumnIds } from "./invoices"
import { ItemReportColumnIds } from "./itemReportList"
import { OrdersColumnIds } from "./orders"
import { PaymentsColumnIds } from "./payments"
import { PriceListColumnIds } from "./price-lists"
import { ProductsColumnIds } from "./products"
import { QuoteColumnIds } from "./quotes"
import { SchedulingPagesColumnIds } from "./scheduling-pages"
import { SubscriptionColumnIds } from "./subscription"
import { SupplierColumnIds } from "./suppliers"

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type TableRowData = any // TODO: define list of possible row interfaces
export type TableRowId =
    | InvoiceColumnIds
    | QuoteColumnIds
    | OrdersColumnIds
    | PaymentsColumnIds
    | ContactsColumnIds
    | CompaniesColumnIds
    | ProductsColumnIds
    | SubscriptionColumnIds
    | CreditMemosColumnIds
    | SupplierColumnIds
    | PriceListColumnIds
    | ItemReportColumnIds
    | AppointmentsColumnIds
    | SchedulingPagesColumnIds
    | InventoryStockDocumentColumnIds
    | ItemReportColumnIds
    | AccountStatementsColumnIds

export type CommonColumnDefinitionAttributes = {
    id: TableRowId
    accessorKey?: string
    accessorFn?: (row: TableRowData) => string
    defaultSort?:boolean
    sortable?: boolean
    sortId?: string
    nestedSortFilters?: {[key: string]: string}
    totalAttributes?: TotalAttributes
    identifier?: boolean
    defaultView?: boolean
    categories: ColumnDefinitionCategory[]
    hidden?: boolean
}

export type TotalAttributes = {
    key: string
    type: TotalType
    parseValue?: (value: string) => string
}

export type CommonDataAttributes = {
    textAlign?: Property.TextAlign
    loadingKey?: string
    defaultValue?: string
}

export type SimpleColumnDefinition = {
    dataType:
        | DataType.CONTACT
        | DataType.BOOLEAN
        | DataType.IMAGE
        | DataType.PRICING
        | DataType.INVENTORY
        | DataType.FILE
    dataAttributes?: CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type StringColumnDefinition = {
    dataType: DataType.STRING
    dataAttributes?: {
        messageMap?: {[key: string]: Message}
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type LinkColumnDefinition = {
    dataType: DataType.LINK
    dataAttributes: {
        redirect?: string
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        modal?: any
        secondaryValueKey?: string
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type DateColumnDefinition = {
    dataType: DataType.DATE
    dataAttributes?: {
        dateExpiryStatuses?: string[]
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type BadgeColumnDefinition = {
    dataType: DataType.BADGE
    dataAttributes: {
        badgeConfigMap: {[key: string]: BadgeConfig}
        statusCategoryKey?: string
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type ArrayColumnDefinition = {
    dataType: DataType.ARRAY
    dataAttributes?: {
        aggregationKey?: string
        optionsKey?: string
        displayAs?: ArrayDisplayType
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type OptionsColumnDefinition = {
    dataType: DataType.OPTIONS
    dataAttributes: {
        optionsKey: string
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type CurrencyColumnDefinition = {
    dataType: DataType.CURRENCY
    dataAttributes?: {
        showPaidAmount?: boolean
        paidAmountKey?: string
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type ComponentColumnDefinition = {
    dataType: DataType.COMPONENT
    dataAttributes : {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        component : any
        props? : {[key: string]: string}
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes

export type LocationColumnDefinition = {
    dataType: DataType.LOCATION | DataType.LINK | DataType.STRING
    dataAttributes : {
        redirect? : string
        messageMap?: {[key: string]: Message}
    } & CommonDataAttributes
} & CommonColumnDefinitionAttributes


export type ColumnDefinition =
    | SimpleColumnDefinition
    | StringColumnDefinition
    | LinkColumnDefinition
    | DateColumnDefinition
    | BadgeColumnDefinition
    | ArrayColumnDefinition
    | OptionsColumnDefinition
    | CurrencyColumnDefinition
    | ComponentColumnDefinition
    | LocationColumnDefinition

export enum DataType {
    STRING = "string",
    CURRENCY = "currency",
    DATE = "date",
    LINK = "link",
    CONTACT = "contact",
    BADGE = "badge",
    ARRAY = "array",
    BOOLEAN = "boolean",
    OPTIONS = "options",
    IMAGE = "image",
    PRICING = "pricing",
    INVENTORY = "inventory",
    COMPONENT = "component",
    LOCATION = "location",
    FILE = "file",
}

export enum TotalType {
    STRING = "string",
    CURRENCY = "currency",
}

export type BadgeConfig = {
    message: Message,
    type: string,
    icon?: string,
}

export type BulkActionDefinition = {
    id: string
    message: Message
    icon: string
    type?: BulkActionType
    guard?: string
    permission?: boolean
}

export enum BulkActionType {
    DANGER = "danger",
}

export type BulkActions = {[key: string]: ReactNode}

export enum ColumnDefinitionCategory {
    DEFAULT = "default",
    CONTACT_RELATED = "contact-related",
    DATE_RELATED = "date-related",
    PAYMENT_RELATED = "payment-related",
    PRODUCT_RELATED = "product-related",
    OTHER = "other",
}

export const ColumnDefinitionCategoryHeaderMap: {[key: string]: Message} = defineMessages({
    [ColumnDefinitionCategory.DEFAULT]: {
        id: "generic-listing.column-selection-modal.category-header.default",
        defaultMessage: "Default View",
    },
    [ColumnDefinitionCategory.CONTACT_RELATED]: {
        id: "generic-listing.column-selection-modal.category-header.contact-related",
        defaultMessage: "Contact Related",
    },
    [ColumnDefinitionCategory.DATE_RELATED]: {
        id: "generic-listing.column-selection-modal.category-header.date-related",
        defaultMessage: "Date Related",
    },
    [ColumnDefinitionCategory.PAYMENT_RELATED]: {
        id: "generic-listing.column-selection-modal.category-header.payment-related",
        defaultMessage: "Payment Related",
    },
    [ColumnDefinitionCategory.PRODUCT_RELATED]: {
        id: "generic-listing.column-selection-modal.category-header.product-related",
        defaultMessage: "Product Related",
    },
    [ColumnDefinitionCategory.OTHER]: {
        id: "generic-listing.column-selection-modal.category-header.other",
        defaultMessage: "Other",
    },
})

export enum ArrayDisplayType {
    STRING = "string",
    BADGE = "badge",
}



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/index.ts